import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../services/apiClient';
import {
  WowpressAwkSubGroup,
  WowpressProductDetail,
  WowpressProductSummary,
  fetchWowpressPrice,
  fetchWowpressProductDetail,
  fetchWowpressProductInfo,
  fetchWowpressProducts,
} from './wowpressQuoteService';
import styles from './WowpressPricePreview.module.scss';

export interface WowpressApplyPayload {
  shape: string;
  quantity: number;
  content: string;
}

function getPathSegments(pathname: string): string[] {
  return pathname.split('>').map((s) => s.trim());
}

// 공급처 상품설명은 HTML로 올 수 있어 태그를 걷어내고 글자만 보여준다
function stripHtml(html: string | null): string | null {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
  return text || null;
}

function WowpressPricePreview() {
  const [productDescription, setProductDescription] = useState<string | null>(null);
  const navigate = useNavigate();
  const [products, setProducts] = useState<WowpressProductSummary[] | null>(null);
  const [productsError, setProductsError] = useState('');
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const [selected, setSelected] = useState<WowpressProductSummary | null>(null);
  const [detail, setDetail] = useState<WowpressProductDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [sizeno, setSizeno] = useState<number | null>(null);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [paperno, setPaperno] = useState<number | null>(null);
  const [colorno, setColorno] = useState<number | null>(null);
  const [requiredAwk, setRequiredAwk] = useState<Record<number, number>>({});
  const [optionalAwk, setOptionalAwk] = useState<Set<number>>(new Set());
  const [qty, setQty] = useState<number>(1);

  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState('');
  const [price, setPrice] = useState<{
    bill: number;
    sup: number;
    tax: number;
    exitday?: number;
  } | null>(null);

  useEffect(() => {
    if (products || productsError) return;
    let cancelled = false;
    fetchWowpressProducts()
      .then((list) => {
        if (!cancelled) setProducts(list);
      })
      .catch((err) => {
        if (!cancelled) setProductsError(err instanceof Error ? err.message : '제품목록을 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, [products, productsError]);

  const usableProducts = useMemo(() => (products ?? []).filter((p) => p.useyn === 'Y'), [products]);

  const categories = useMemo(() => {
    const names = new Set<string>();
    usableProducts.forEach((p) => names.add(getPathSegments(p.pathname)[0] ?? '기타'));
    return [...names].sort((a, b) => a.localeCompare(b, 'ko'));
  }, [usableProducts]);

  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  const activeCategoryGroups = useMemo(() => {
    if (!activeCategory) return [];
    const map = new Map<string, WowpressProductSummary[]>();
    usableProducts.forEach((p) => {
      const segs = getPathSegments(p.pathname);
      if (segs[0] !== activeCategory) return;
      const groupName = segs.length >= 3 ? segs[1] : '';
      if (!map.has(groupName)) map.set(groupName, []);
      map.get(groupName)!.push(p);
    });
    const q = query.trim();
    return [...map.entries()]
      .map(([name, items]) => [name, q ? items.filter((p) => p.name.includes(q)) : items] as [string, WowpressProductSummary[]])
      .filter(([, items]) => items.length > 0)
      .sort((a, b) => {
        if (a[0] === '') return 1;
        if (b[0] === '') return -1;
        return a[0].localeCompare(b[0], 'ko');
      });
  }, [usableProducts, activeCategory, query]);

  const cover0 = useMemo(() => detail?.coverinfo?.find((c) => c.covercd === 0), [detail]);
  const isSupported = !!detail && (detail.coverinfo ?? []).length === 1 && !!cover0;

  const sizeList = useMemo(() => detail?.sizeinfo?.find((s) => s.covercd === 0)?.sizelist ?? [], [detail]);
  const paperList = useMemo(() => detail?.paperinfo?.find((p) => p.covercd === 0)?.paperlist ?? [], [detail]);
  const selectedPaper = paperList.find((p) => p.paperno === paperno) ?? null;
  const frontColorPage = useMemo(() => {
    const pagelist = detail?.colorinfo?.find((c) => c.covercd === 0)?.pagelist ?? [];
    // pagecd 0: 단면/양면이 하나로 통합된 상품, pagecd 1: 전면/후면이 분리된 상품 중 전면
    return pagelist.find((p) => p.pagecd === 0) ?? pagelist.find((p) => p.pagecd === 1);
  }, [detail]);
  const hasBackPage = useMemo(
    () => !!detail?.colorinfo?.find((c) => c.covercd === 0)?.pagelist?.some((p) => p.pagecd === 2),
    [detail],
  );
  const awkGroups: WowpressAwkSubGroup[] = useMemo(
    () =>
      (detail?.awkjobinfo ?? [])
        .filter((g) => g.covercd === 0)
        .flatMap((g) => g.jobgrouplist ?? []),
    [detail],
  );
  const requiredGroups = useMemo(() => awkGroups.filter((g) => g.type === 'radio'), [awkGroups]);
  const optionalGroups = useMemo(() => awkGroups.filter((g) => g.type !== 'radio'), [awkGroups]);

  const selectedSize = useMemo(() => sizeList.find((s) => s.sizeno === sizeno) ?? null, [sizeList, sizeno]);
  const selectedColor = useMemo(
    () => frontColorPage?.colorlist?.find((c) => c.colorno === colorno) ?? null,
    [frontColorPage, colorno],
  );
  const qtyRange = useMemo(() => {
    const list = detail?.ordqty ?? [];
    const match = list.find((q) => q.sizeno === sizeno) ?? list.find((q) => q.sizeno == null);
    return match ?? null;
  }, [detail, sizeno]);

  const selectProduct = async (product: WowpressProductSummary) => {
    setSelected(product);
    setListOpen(false);
    setDetail(null);
    setProductDescription(null);
    // 상품설명은 부가 정보라 실패해도 옵션 선택은 그대로 진행한다
    fetchWowpressProductInfo(product.prodno)
      .then((info) => setProductDescription(stripHtml(info.description)))
      .catch(() => setProductDescription(null));
    setDetailError('');
    setPrice(null);
    setPriceError('');
    setDetailLoading(true);
    try {
      const raw = await fetchWowpressProductDetail(product.prodno);
      const d: WowpressProductDetail = {
        ...raw,
        coverinfo: raw.coverinfo ?? [],
        ordqty: raw.ordqty ?? [],
        sizeinfo: raw.sizeinfo ?? [],
        paperinfo: raw.paperinfo ?? [],
        colorinfo: raw.colorinfo ?? [],
        awkjobinfo: raw.awkjobinfo ?? [],
      };
      setDetail(d);
      const size0 = d.sizeinfo.find((s) => s.covercd === 0)?.sizelist?.[0] ?? null;
      setSizeno(size0?.sizeno ?? null);
      const paper0 = d.paperinfo.find((p) => p.covercd === 0)?.paperlist?.[0] ?? null;
      setPaperno(paper0?.paperno ?? null);
      const colorPagelist = d.colorinfo.find((c) => c.covercd === 0)?.pagelist ?? [];
      const front = colorPagelist.find((p) => p.pagecd === 0) ?? colorPagelist.find((p) => p.pagecd === 1);
      setColorno(front?.colorlist?.[0]?.colorno ?? null);
      const nextRequired: Record<number, number> = {};
      d.awkjobinfo
        .filter((g) => g.covercd === 0)
        .flatMap((g) => g.jobgrouplist ?? [])
        .filter((g) => g.type === 'radio')
        .forEach((g) => {
          const first = (g.awkjoblist ?? [])[0];
          if (first) nextRequired[g.jobgroupno] = first.jobno;
        });
      setRequiredAwk(nextRequired);
      setOptionalAwk(new Set());
      const ordqty0 = d.ordqty.find((q) => q.sizeno === size0?.sizeno) ?? d.ordqty.find((q) => q.sizeno == null);
      setQty(ordqty0?.ordqtymin ?? 1);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : '제품상세를 불러오지 못했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  const checkPrice = async () => {
    if (!detail || !selectedSize || !paperno || !selectedColor) return;
    setPriceLoading(true);
    setPriceError('');
    setPrice(null);
    try {
      const width = selectedSize.non_standard ? Number(customWidth) || null : selectedSize.width;
      const height = selectedSize.non_standard ? Number(customHeight) || null : selectedSize.height;
      const prsjobJobno = selectedColor.req_prsjob?.[0]?.jobno;
      if (!prsjobJobno) {
        setPriceError('선택한 도수에 대한 인쇄옵션 정보를 찾을 수 없습니다.');
        return;
      }
      const awkjob = [
        ...Object.values(requiredAwk).map((jobno) => ({ jobno, covercd: 0 })),
        ...Array.from(optionalAwk).map((jobno) => ({ jobno, covercd: 0 })),
      ];
      const result = await fetchWowpressPrice(detail.prodno, {
        ordqty: String(qty),
        ordcnt: '1',
        ordtitle: `${detail.prodname} 견적 미리보기`,
        prsjob: [
          {
            jobno: prsjobJobno,
            covercd: 0,
            sizeno: selectedSize.sizeno,
            wsize: width,
            hsize: height,
            jobqty: String(qty),
            paperno,
            colorno0: selectedColor.colorno,
            colorno0add: '',
          },
        ],
        awkjob,
      });
      if (String(result.status) !== '200') {
        setPriceError(result.errmsg || '이 옵션 조합으로는 주문이 불가능합니다.');
        return;
      }
      setPrice({
        bill: result.ordcost_bill ?? 0,
        sup: result.ordcost_sup ?? 0,
        tax: result.ordcost_tax ?? 0,
        exitday: result.exitday,
      });
    } catch (err) {
      setPriceError(err instanceof ApiError ? err.message : '가격 조회 중 오류가 발생했습니다.');
    } finally {
      setPriceLoading(false);
    }
  };

  const handleApply = () => {
    if (!detail || !selectedSize || !price) return;
    const paperName = paperList.find((p) => p.paperno === paperno)?.papername ?? '';
    const colorName = selectedColor?.colorname ?? '';
    const shape = `${detail.prodname} (${selectedSize.sizename} · ${paperName} · ${colorName})`;
    const content = [
      '[인쇄 상품 실시간 견적]',
      `제품: ${detail.prodname}`,
      `옵션: 규격 ${selectedSize.sizename} · 재질 ${paperName} · 도수 ${colorName}`,
      `수량: ${qty}${detail.unit || '개'}`,
      `예상 청구가: ${price.bill.toLocaleString()}원 (공급가 ${price.sup.toLocaleString()}원 + 부가세 ${price.tax.toLocaleString()}원)`,
      price.exitday != null ? `예상 출고: 접수 후 ${price.exitday}일 이내` : null,
      '※ 실제 결제금액은 배송비/추가옵션에 따라 달라질 수 있습니다. 담당자가 확인 후 최종 견적을 다시 안내드립니다.',
    ]
      .filter(Boolean)
      .join('\n');
    const payload: WowpressApplyPayload = { shape, quantity: qty, content };
    navigate('/order/form', { state: { wowpressQuote: payload, designType: 'HAVE' } });
  };

  return (
    <section className={styles.panel}>
      <header className={styles.head}>
        <h3 className={styles.title}>인쇄 상품 선택 · 실시간 가격 확인</h3>
        <p className={styles.hint}>
          명함 · 스티커 · 전단 등 인쇄 상품을 선택하면 규격 · 용지 · 도수 · 수량에 따른 가격을 바로 확인할 수 있습니다.
        </p>
      </header>

      {selected && !listOpen ? (
        <div className={styles.selectedBar}>
          <div>
            <span className={styles.selectedName}>{selected.name}</span>
            <span className={styles.selectedPath}>{selected.pathname}</span>
          </div>
          <button type="button" className={styles.btnChange} onClick={() => setListOpen(true)}>
            다른 제품 선택
          </button>
        </div>
      ) : (
        <div className={styles.directory}>
          {productsError && <div className={`${styles.msg} ${styles.error}`}>{productsError}</div>}
          {!productsError && !products && <div className={styles.msg}>제품목록을 불러오는 중...</div>}

          {products && (
            <>
              <aside className={styles.sidebar}>
                {categories.map((c) => (
                  <button
                    type="button"
                    key={c}
                    className={`${styles.sidebarItem} ${activeCategory === c ? styles.active : ''}`}
                    onClick={() => {
                      setActiveCategory(c);
                      setQuery('');
                    }}
                  >
                    {c}
                  </button>
                ))}
              </aside>

              <div className={styles.mainArea}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder={activeCategory ? `${activeCategory} 내에서 검색` : '제품명 검색'}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div className={styles.groupColumns}>
                  {activeCategoryGroups.length === 0 && (
                    <div className={styles.searchMsg}>검색 결과가 없습니다.</div>
                  )}
                  {activeCategoryGroups.map(([groupName, items]) => (
                    <div className={styles.group} key={groupName || '_ungrouped'}>
                      {groupName && <div className={styles.groupTitle}>{groupName}</div>}
                      <div className={styles.groupList}>
                        {items.map((p) => (
                          <button
                            type="button"
                            key={p.prodno}
                            className={`${styles.groupItem} ${selected?.prodno === p.prodno ? styles.active : ''}`}
                            onClick={() => selectProduct(p)}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {detailLoading && <div className={styles.msg}>제품 옵션을 불러오는 중...</div>}
      {detailError && <div className={`${styles.msg} ${styles.error}`}>{detailError}</div>}

      {selected && productDescription && (
        <div className={styles.productDesc}>
          <strong>상품 설명</strong>
          <p>{productDescription}</p>
        </div>
      )}

      {detail && !isSupported && (
        <div className={`${styles.msg} ${styles.error}`}>
          이 상품은 표지/내지 등 구성이 복잡해 온라인 가격 확인이 어렵습니다. 견적문의로 사양을 남겨주시면
          담당자가 확인 후 안내드립니다.
        </div>
      )}

      {detail && isSupported && (
        <div className={styles.optionGrid}>
          <div className={styles.optionField}>
            <label>규격</label>
            <select
              className={styles.select}
              value={sizeno ?? ''}
              onChange={(e) => setSizeno(Number(e.target.value))}
            >
              {sizeList.map((s) => (
                <option key={s.sizeno} value={s.sizeno}>
                  {s.sizename}
                </option>
              ))}
            </select>
          </div>

          {selectedSize?.non_standard && (
            <div className={styles.optionField}>
              <label>가로 × 세로 (mm)</label>
              <div className={styles.widthHeight}>
                <input
                  type="number"
                  className={styles.select}
                  placeholder="가로"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  className={styles.select}
                  placeholder="세로"
                  value={customHeight}
                  onChange={(e) => setCustomHeight(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className={styles.optionField}>
            <label>재질 (용지)</label>
            <select
              className={styles.select}
              value={paperno ?? ''}
              onChange={(e) => setPaperno(Number(e.target.value))}
            >
              {paperList.map((p) => (
                <option key={p.paperno} value={p.paperno}>
                  {p.papername}
                </option>
              ))}
            </select>
            {selectedPaper && (selectedPaper.papergroup || selectedPaper.pgram) && (
              <p className={styles.paperInfo}>
                {selectedPaper.papergroup && <span>용지 계열 {selectedPaper.papergroup}</span>}
                {!!selectedPaper.pgram && <span>평량 {selectedPaper.pgram}g/㎡</span>}
              </p>
            )}
          </div>

          <div className={styles.optionField}>
            <label>도수{hasBackPage ? ' (전면 기준)' : ''}</label>
            <select
              className={styles.select}
              value={colorno ?? ''}
              onChange={(e) => setColorno(Number(e.target.value))}
            >
              {frontColorPage?.colorlist?.map((c) => (
                <option key={c.colorno} value={c.colorno}>
                  {c.colorname}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.optionField}>
            <label>수량 ({detail.unit || '개'})</label>
            <input
              type="number"
              className={styles.select}
              value={qty}
              min={qtyRange?.ordqtymin ?? 1}
              max={qtyRange?.ordqtymax ?? undefined}
              onChange={(e) => setQty(Number(e.target.value))}
            />
          </div>

          {requiredGroups.map((g) => (
            <div className={styles.optionField} key={g.jobgroupno}>
              <label>{g.jobgroup}</label>
              <select
                className={styles.select}
                value={requiredAwk[g.jobgroupno] ?? ''}
                onChange={(e) =>
                  setRequiredAwk((prev) => ({ ...prev, [g.jobgroupno]: Number(e.target.value) }))
                }
              >
                {(g.awkjoblist ?? []).map((j) => (
                  <option key={j.jobno} value={j.jobno}>
                    {j.jobname}
                  </option>
                ))}
              </select>
            </div>
          ))}

          {optionalGroups.length > 0 && (
            <div className={`${styles.optionField} ${styles.full}`}>
              <label>추가 후가공 (선택)</label>
              <div className={styles.awkPills}>
                {optionalGroups.flatMap((g) => g.awkjoblist ?? []).map((j) => {
                  const checked = optionalAwk.has(j.jobno);
                  return (
                    <label key={j.jobno} className={`${styles.awkPill} ${checked ? styles.checked : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          setOptionalAwk((prev) => {
                            const next = new Set(prev);
                            if (next.has(j.jobno)) next.delete(j.jobno);
                            else next.add(j.jobno);
                            return next;
                          });
                        }}
                      />
                      {j.jobname}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {detail && isSupported && (
        <div className={styles.actions}>
          <button type="button" className={styles.btnCheck} onClick={checkPrice} disabled={priceLoading}>
            {priceLoading ? '조회 중...' : '실시간 가격 확인'}
          </button>
        </div>
      )}

      {priceError && <div className={`${styles.msg} ${styles.error}`}>{priceError}</div>}

      {price && (
        <div className={styles.priceResult}>
          <div className={styles.priceMain}>
            <span>예상 청구가</span>
            <strong>{price.bill.toLocaleString()}원</strong>
          </div>
          <div className={styles.priceSub}>
            공급가 {price.sup.toLocaleString()}원 + 부가세 {price.tax.toLocaleString()}원
            {price.exitday != null ? ` · 접수 후 ${price.exitday}일 이내 출고 예상` : ''}
          </div>
          <button type="button" className={styles.btnApply} onClick={handleApply}>
            이 옵션으로 주문서 작성
          </button>
        </div>
      )}
    </section>
  );
}

export default WowpressPricePreview;
